import React, { useState, useEffect, useContext } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { apiConfig } from '../../api/ApiConfig'
import { UserDetailContext } from '../../context/UserDetailContext'

const DossierGenerator = () => {
  const { executeDossier, getDossierStatus, getDossiersByUserId } = apiConfig()
  const userDetailContext = useContext(UserDetailContext)

  const [formData, setFormData] = useState({
    job_id: '',
    indication: '',
    molecule_identifier: '',
    target_market_share: '',
    discount_rate: ''
  })

  const [jobId, setJobId] = useState(null)
  const [status, setStatus] = useState(null)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reports, setReports] = useState([])

  useEffect(() => {
    const fetchDossiers = async () => {
      if (userDetailContext?.userDetail?.userId) {
        try {
          const fetchedReports = await getDossiersByUserId(userDetailContext.userDetail.userId)
          if (Array.isArray(fetchedReports)) {
            const formattedReports = fetchedReports.map(report => ({
              job_id: report.job_id,
              indication: report.indication,
              molecule_identifier: report.molecule_identifier || report.molecule?.identifier,
              dossier_url: report.dossier_url,
              timestamp: report.timestamp || new Date().toLocaleString()
            }))
            setReports(formattedReports)
          }
        } catch (err) {
          console.error('Failed to fetch initial dossiers:', err)
        }
      }
    }

    fetchDossiers()
  }, [userDetailContext?.userDetail?.userId])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setResult(null)
    setProgress(0)
    setStatus('initializing')

    const payload = {
      job_id: formData.job_id,
      molecule: {
        identifier: formData.molecule_identifier,
        indication: formData.indication
      },
      scope: {
        sections: ['all']
      },
      target_market_share: parseFloat(formData.target_market_share),
      discount_rate: parseFloat(formData.discount_rate)
    }

    try {
      const response = await executeDossier(payload)
      if (response.job_id) {
        setJobId(response.job_id)
        setStatus('running')
      } else {
        throw new Error('No job ID received')
      }
    } catch (err) {
      console.error(err)
      setError(err.message)
      setLoading(false)
      setStatus('failed')
    }
  }

  useEffect(() => {
    let intervalId
    let isMounted = true

    const pollStatus = async () => {
      if (!jobId || status === 'completed' || status === 'failed') return

      try {
        const jobStatus = await getDossierStatus(jobId)

        if (!isMounted) return

        setStatus(jobStatus.status)
        setProgress(jobStatus.progress)

        if (jobStatus.status === 'completed') {
          setResult(jobStatus.result)
          setLoading(false)
          setStatus('completed')
          setJobId(null) // Stop polling

          if (jobStatus.result && jobStatus.result.dossier_url) {
            setReports(prev => [...prev, {
              job_id: jobId,
              indication: formData.indication,
              molecule_identifier: formData.molecule_identifier,
              dossier_url: jobStatus.result.dossier_url,
              timestamp: new Date().toLocaleString()
            }])
          }
        } else if (jobStatus.status === 'failed') {
          setError(jobStatus.error || 'Job failed')
          setLoading(false)
          setStatus('failed')
          setJobId(null) // Stop polling
        }
      } catch (err) {
        if (isMounted) {
            console.error('Polling error:', err)
        }
      }
    }

    if (jobId && (status === 'running' || status === 'initializing')) {
      intervalId = setInterval(pollStatus, 2000)
    }

    return () => {
      isMounted = false
      if (intervalId) clearInterval(intervalId)
    }
  }, [jobId, status])

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          360° Dossier Generator
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Job ID"
            name="job_id"
            value={formData.job_id}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Molecule Identifier"
            name="molecule_identifier"
            value={formData.molecule_identifier}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Indication"
            name="indication"
            value={formData.indication}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Target Market Share"
            name="target_market_share"
            value={formData.target_market_share}
            onChange={handleChange}
            margin="normal"
            required
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Discount Rate"
            name="discount_rate"
            value={formData.discount_rate}
            onChange={handleChange}
            margin="normal"
            required
            type="number"
            inputProps={{ step: 0.01, min: 0, max: 1 }}
            disabled={loading}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? 'Generating Dossier...' : 'Generate Dossier'}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status: {status} ({progress}%)
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 4 }}>
            <Alert severity="success">
              <Typography variant="h6" gutterBottom>
                Dossier Generated Successfully!
              </Typography>
              <Typography variant="body1">
                {result.summary}
              </Typography>
              {result.dossier_url && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    href={result.dossier_url}
                    target="_blank"
                    rel="noopener"
                    variant="outlined"
                    color="success"
                    download
                  >
                    Download Dossier
                  </Button>
                </Box>
              )}
            </Alert>
          </Box>
        )}

        {reports.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" gutterBottom>
              Generated Reports
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job ID</TableCell>
                    <TableCell>Indication</TableCell>
                    <TableCell>Molecule</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report, index) => (
                    <TableRow key={index}>
                      <TableCell>{report.job_id}</TableCell>
                      <TableCell>{report.indication}</TableCell>
                      <TableCell>{report.molecule_identifier}</TableCell>
                      <TableCell>{report.timestamp}</TableCell>
                      <TableCell align="right">
                        <Button
                          href={report.dossier_url}
                          target="_blank"
                          rel="noopener"
                          variant="text"
                          color="primary"
                          download
                          size="small"
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

      </Paper>
    </Box>
  )
}

export default DossierGenerator
